import { AssessmentLevelResult, AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { useTheme } from '@mui/material'
import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ProgressTrackerFilterType, useProgressTrackerModal } from './ProgressTrackerModalContext'

type BaselineAssessmentDistributionGraphProps = {
  assessmentLevelResults?: AssessmentLevelResult[]
  progressTrackerId?: number
  assessmentTemplate?: AssessmentTemplate
  periodNames?: string[]
}

const BaselineAssessmentDistributionGraph: React.FC<BaselineAssessmentDistributionGraphProps> = ({
  assessmentLevelResults,
  progressTrackerId,
  assessmentTemplate,
  periodNames,
}) => {
  const { palette, typography } = useTheme()
  const { formatMessage } = useIntl()
  const { openProgressTrackerModal } = useProgressTrackerModal()
  const levels = [1, 2, 3, 4, 5].map(item => formatMessage({ id: `schemas.progressTracker.level` }, { level: item }))

  const data = useMemo(() => {
    return (
      [1, 2, 3, 4, 5]?.map(level => assessmentLevelResults?.find(item => item.level === level)?.providerCount ?? 0) ?? [
        0, 0, 0, 0, 0,
      ]
    )
  }, [assessmentLevelResults])

  const options: EChartsOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: { dataIndex: number; name: string }) => {
          const originalValue = data[params.dataIndex]
          if (originalValue === 0) {
            return '' // Hide tooltip for zero values
          }
          return `${params.name}: ${originalValue}`
        },
      },
      grid: {
        left: '0%',
        right: '0%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: levels,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      yAxis: {
        type: 'value',
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
          fontFamily: typography.fontFamily,
          fontSize: typography.body1.fontSize,
        },
      },
      series: [
        {
          data: data.map(count => ({
            value: count === 0 ? 1 : count,
            label: {
              show: count > 0,
              position: 'top',
              fontFamily: typography.fontFamily,
              fontSize: typography.body1.fontSize,
              color: palette.text.primary,
            },
          })),
          type: 'bar',
          itemStyle: {
            color: (params: { dataIndex: number }) => {
              if (data[params.dataIndex] === 0) {
                return palette.grey[100]
              }
              const colors = [
                palette.semantic.error,
                palette.error.dark,
                palette.warning.dark,
                palette.success.dark,
                palette.brandDecorative.emerald,
              ]
              return colors[params.dataIndex]
            },
          },
          barWidth: '90%',
        },
      ],
    }),
    [data, formatMessage, palette, typography],
  )

  const handleLevelClick = (level: number) => {
    if (!progressTrackerId || !assessmentTemplate || !periodNames) return

    openProgressTrackerModal(
      ProgressTrackerFilterType.AssessmentLevel,
      assessmentTemplate,
      periodNames,
      level,
      `${formatMessage({ id: 'schemas.progressTracker.distribution' })} - ${formatMessage({ id: 'schemas.progressTracker.level' }, { level })}`,
    )
  }

  const events = useMemo(() => {
    if (!progressTrackerId) return undefined

    return {
      click: (params: { dataIndex: number }) => {
        const level = params.dataIndex + 1
        handleLevelClick(level)
      },
    }
  }, [progressTrackerId, handleLevelClick])

  return <ReactEcharts option={options} onEvents={events} style={{ height: 200 }} />
}

export default BaselineAssessmentDistributionGraph
